import { useCallback, useMemo, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
	createValuationInputAdjustment,
	deleteValuationInputAdjustment,
	getValuationInputs,
	upsertValuationInputs,
} from '@dealstack/frontend/shared/dataExchange/services/valuations.service';
import { HighPrecisionDecimal } from '@dealstack/shared/lib/format/currency/highPrecisionDecimal';
import {
	CreateValuationInputAdjustmentDto,
	UpsertValuationInputAdjustmentItemDto,
	UpsertValuationInputItemDto,
	ValuationInputAdjustmentWithClassifierRes,
	ValuationInputWithAdjustmentsRes,
} from '@dealstack/shared/lib/generated';
import { VALUATION_INPUT_DEBOUNCE_MS } from '@dealstack/shared/lib/debounceValues';
import {
	VALUATION_COMPUTE_START_QUERY_KEY,
	VALUATION_INPUTS_QUERY_KEY,
	VALUATION_SAVE_MUTATION_KEY,
} from '@dealstack/frontend/app/pages/Valuations/queries/queryKeys';
import { NEW_VALUATION_ID } from '@dealstack/frontend/app/pages/Valuations/shared/valuationRoutes';
import { useDebouncedSave } from '@dealstack/frontend/shared/hooks/useDebouncedSave';
import { useInflightWrites } from '@dealstack/frontend/shared/hooks/useInflightWrites';
import { replaceCachePayload } from '@dealstack/frontend/shared/dataExchange/cacheHelpers';
import { toastError } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';

/**
 * Snapshot that the form passes down on each debounced save. Mirrors the
 * upsert DTO shape so the hook can hand it straight to the service without
 * any per-row bookkeeping.
 */
export interface ValuationInputsSavePayload {
	inputs: UpsertValuationInputItemDto[];
	adjustments: UpsertValuationInputAdjustmentItemDto[];
}

export function useValuationInputsQuery(
	dealId: string | undefined,
	valuationId: string | undefined,
	historicalAndCustomFXRates: Record<string, { history: number | null; custom: string | null }>
) {
	const queryKey = useMemo(
		() => [VALUATION_INPUTS_QUERY_KEY, dealId ?? '', valuationId ?? ''],
		[dealId, valuationId]
	);
	const queryClient = useQueryClient();

	// Inputs changed → start a fresh compute (new workflowId reads the new inputs). Refetching the
	// start query cascades into a new stream + result; re-streaming the old workflowId would just
	// replay the stale compute. WAT-730.
	const invalidateWorkflow = useCallback(() => {
		void queryClient.invalidateQueries({
			queryKey: [VALUATION_COMPUTE_START_QUERY_KEY, dealId ?? '', valuationId ?? ''],
		});
	}, [queryClient, dealId, valuationId]);

	const inputsQuery = useQuery({
		queryKey,
		queryFn: () => getValuationInputs({ valuationId: valuationId ?? '', dealId: dealId ?? '' }),
		enabled: !!dealId && !!valuationId && valuationId !== NEW_VALUATION_ID,
	});

	const pendingRef = useRef<ValuationInputsSavePayload | null>(null);
	// Adjustment create/delete bypass the debounced bulk upsert, so flush() has to
	// wait on them separately or a compute can start before they commit.
	const { track: trackAdjustmentWrite, settled: adjustmentWritesSettled } = useInflightWrites();

	const { schedule, flush: flushDebouncedSave } = useDebouncedSave({
		mutationKey: [VALUATION_SAVE_MUTATION_KEY, valuationId ?? ''],
		debounceMs: VALUATION_INPUT_DEBOUNCE_MS,
		mutationFn: async () => {
			const snapshot = pendingRef.current;
			pendingRef.current = null;
			if (!snapshot || !valuationId || !dealId) {
				return null;
			}
			if (snapshot.inputs.length === 0 && snapshot.adjustments.length === 0) {
				return null;
			}

			const response = await upsertValuationInputs({
				valuationId,
				dealId,
				data: snapshot,
			});

			return response.payload ?? null;
		},
		onSuccess: (response) => {
			if (response) {
				replaceCachePayload<ValuationInputWithAdjustmentsRes[]>(
					queryClient,
					queryKey,
					response.state
				);
				invalidateWorkflow();
			}
		},
		onError: (error) => toastError('Error updating valuation inputs', error),
	});

	const saveFormSnapshot = useCallback(
		(snapshot: ValuationInputsSavePayload) => {
			pendingRef.current = snapshot;
			schedule();
		},
		[schedule]
	);

	const deleteInputAdjustment = useMutation({
		mutationKey: [VALUATION_SAVE_MUTATION_KEY, valuationId ?? ''],
		mutationFn: async ({
			valuationInputAdjustmentId,
			valuationInputId,
		}: {
			valuationInputAdjustmentId: string;
			valuationInputId: string;
		}) => {
			await trackAdjustmentWrite(
				deleteValuationInputAdjustment({
					valuationId: valuationId ?? '',
					valuationInputId,
					valuationInputAdjustmentId,
					dealId: dealId ?? '',
				})
			);
		},
		onSuccess: async () => {
			await inputsQuery.refetch();
			invalidateWorkflow();
		},
	});

	// Adjustment creation goes through the per-row POST instead of the bulk
	// upsert. The table's row inputs are gated on `field.adjustmentId`, and the
	// bulk PUT response state isn't merged back into the form's useFieldArray —
	// so locally-appended rows would stay disabled until the next reset.
	const createInputAdjustment = useMutation<
		ValuationInputAdjustmentWithClassifierRes | undefined,
		unknown,
		{ valuationInputId: string; data: CreateValuationInputAdjustmentDto }
	>({
		mutationKey: [VALUATION_SAVE_MUTATION_KEY, valuationId ?? ''],
		mutationFn: async ({ valuationInputId, data }) => {
			const response = await trackAdjustmentWrite(
				createValuationInputAdjustment({
					dealId: dealId ?? '',
					valuationId: valuationId ?? '',
					valuationInputId,
					data,
				})
			);
			return response.payload;
		},
		onSuccess: async () => {
			await inputsQuery.refetch();
			invalidateWorkflow();
		},
		onError: (error) => toastError('Error creating adjustment', error),
	});

	// Everything the user has typed or clicked is persisted once this resolves.
	// Adjustment writes report their own failures via toast, so only the bulk
	// upsert's outcome gates the caller.
	const flush = useCallback(async () => {
		const [saved] = await Promise.all([flushDebouncedSave(), adjustmentWritesSettled()]);
		return saved;
	}, [flushDebouncedSave, adjustmentWritesSettled]);

	const convertAmount = useCallback(
		(amount: HighPrecisionDecimal, fromCurrency: string) => {
			const fromRateData = historicalAndCustomFXRates[fromCurrency];
			const fromRate =
				(fromRateData?.custom ? Number(fromRateData.custom) : fromRateData?.history) ?? 1;
			if (!Number.isFinite(fromRate) || fromRate <= 0) {
				return amount;
			}
			return amount.div(fromRate);
		},
		[historicalAndCustomFXRates]
	);

	return {
		inputs: inputsQuery.data?.payload ?? [],
		isLoading: inputsQuery.isLoading,
		refetch: inputsQuery.refetch,
		saveFormSnapshot,
		flush,
		createInputAdjustment,
		deleteInputAdjustment,
		convertAmount,
	};
}
